import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployees } from '@/hooks/useEmployees';
import { PasswordChangeDialog } from '@/components/employee/PasswordChangeDialog';
import { EmployeePerformanceAnalysis } from '@/components/employee/EmployeePerformanceAnalysis';
import { format } from 'date-fns';

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'yyyy/MM/dd');
  } catch {
    return dateString;
  }
};
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Award,
  Shield,
  Key,
  BarChart3,
  FileText,
  Clock,
  DollarSign
} from 'lucide-react';

export default function EmployeeProfile() {
  const { user } = useAuth();
  const { employees, loading, error } = useEmployees();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  console.log('EmployeeProfile - Current user:', user);
  console.log('EmployeeProfile - Employees data:', employees);
  console.log('EmployeeProfile - Loading:', loading);
  console.log('EmployeeProfile - Error:', error);

  // العثور على بيانات الموظف الحالي
  const currentEmployee = employees.find(emp => 
    emp.user_id === user?.id || 
    emp.id === user?.employeeData?.id
  );
  console.log('EmployeeProfile - Current employee found:', currentEmployee);

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin mx-auto h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              <h3 className="mt-4 text-lg font-semibold">جاري تحميل بيانات الموظف...</h3>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-destructive" />
              <h3 className="mt-4 text-lg font-semibold">خطأ في تحميل بيانات الموظف</h3>
              <p className="text-muted-foreground">{error?.message || 'حدث خطأ غير متوقع'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentEmployee) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">لم يتم العثور على ملف الموظف</h3>
              <p className="text-muted-foreground">
                {user ? 
                  `لم يتم العثور على ملف موظف مرتبط بالمستخدم ${user.username}. يرجى التواصل مع إدارة النظام.` :
                  'يرجى تسجيل الدخول أولاً للوصول إلى ملف الموظف.'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'present': { label: 'حاضر', variant: 'default' as const },
      'absent': { label: 'غائب', variant: 'destructive' as const },
      'late': { label: 'متأخر', variant: 'secondary' as const }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getApprovalStatusBadge = (status?: string) => {
    const statusConfig = {
      'approved': { label: 'معتمد', variant: 'default' as const },
      'pending': { label: 'في انتظار الموافقة', variant: 'secondary' as const },
      'rejected': { label: 'مرفوض', variant: 'destructive' as const }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || { label: 'غير محدد', variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">ملف الموظف</h1>
          <p className="text-muted-foreground">عرض وإدارة بياناتك الشخصية</p>
        </div>
      </div>

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="personal">المعلومات الشخصية</TabsTrigger>
          <TabsTrigger value="job">المعلومات الوظيفية</TabsTrigger>
          <TabsTrigger value="performance">تحليل الأداء</TabsTrigger>
        </TabsList>

        {/* المعلومات الشخصية */}
        <TabsContent value="personal" className="space-y-6">
          {/* بطاقة الملف الشخصي */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                الملف الشخصي
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 mb-6">
                <Avatar className="w-24 h-24">
                  {currentEmployee.profile_image && (
                    <AvatarImage src={currentEmployee.profile_image} alt={currentEmployee.name} />
                  )}
                  <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold text-lg">
                    {currentEmployee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{currentEmployee.name}</h2>
                  <p className="text-muted-foreground">{currentEmployee.position}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusBadge(currentEmployee.status)}
                    {getApprovalStatusBadge(currentEmployee.approval_status)}
                  </div>
                </div>
                <Button 
                  onClick={() => setPasswordDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  تغيير كلمة المرور
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">البريد الإلكتروني</p>
                      <p className="text-muted-foreground">{currentEmployee.email || 'غير محدد'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">رقم الهاتف</p>
                      <p className="text-muted-foreground">{currentEmployee.phone || 'غير محدد'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">تاريخ الميلاد</p>
                      <p className="text-muted-foreground">
                        {currentEmployee.birth_date ? formatDate(currentEmployee.birth_date) : 'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">القسم</p>
                      <p className="text-muted-foreground">{currentEmployee.department || 'غير محدد'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">تاريخ الانضمام</p>
                      <p className="text-muted-foreground">{formatDate(currentEmployee.join_date)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">آخر ظهور</p>
                      <p className="text-muted-foreground">
                        {currentEmployee.last_seen ? formatDate(currentEmployee.last_seen) : 'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* المؤهلات والخبرات */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                المؤهلات والخبرات
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4" />
                  المؤهلات العلمية
                </h4>
                <p className="text-muted-foreground">
                  {currentEmployee.qualifications || 'لم يتم إدخال معلومات المؤهلات'}
                </p>
              </div>

              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4" />
                  الخبرات السابقة
                </h4>
                <p className="text-muted-foreground">
                  {currentEmployee.previous_experience || 'لم يتم إدخال معلومات الخبرات السابقة'}
                </p>
              </div>

              {currentEmployee.electronic_accounts && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4" />
                    الحسابات الإلكترونية
                  </h4>
                  <p className="text-muted-foreground">
                    {typeof currentEmployee.electronic_accounts === 'string' 
                      ? currentEmployee.electronic_accounts 
                      : JSON.stringify(currentEmployee.electronic_accounts)
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* المعلومات الوظيفية */}
        <TabsContent value="job" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                المعلومات الوظيفية
              </CardTitle>
              <CardDescription>
                هذه المعلومات يمكن تعديلها فقط من قبل المدير العام للنظام
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">رقم الموظف</p>
                    <p className="text-muted-foreground">{currentEmployee.employee_number || 'غير محدد'}</p>
                  </div>

                  <div>
                    <p className="font-medium">المسمى الوظيفي</p>
                    <p className="text-muted-foreground">{currentEmployee.job_title || 'غير محدد'}</p>
                  </div>

                  <div>
                    <p className="font-medium">تاريخ التوظيف</p>
                    <p className="text-muted-foreground">
                      {currentEmployee.hiring_date ? formatDate(currentEmployee.hiring_date) : 'غير محدد'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">ساعات العمل</p>
                      <p className="text-muted-foreground">
                        {currentEmployee.working_hours ? `${currentEmployee.working_hours} ساعة/يوم` : 'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">الراتب</p>
                      <p className="text-muted-foreground">
                        {currentEmployee.salary ? `${currentEmployee.salary} ر.س` : 'غير محدد'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="font-medium">المسؤوليات الوظيفية</p>
                    <p className="text-muted-foreground">
                      {currentEmployee.job_responsibilities || 'لم يتم تحديد المسؤوليات'}
                    </p>
                  </div>

                  <div>
                    <p className="font-medium">الوصف الوظيفي</p>
                    <p className="text-muted-foreground">
                      {currentEmployee.job_description || 'لم يتم تحديد الوصف الوظيفي'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ملفات الموظف */}
              <div className="mt-6 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  الملفات المرفقة
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {currentEmployee.certificate_file && (
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-sm">الشهادة</p>
                      <Button variant="link" size="sm" className="p-0 h-auto">
                        عرض الملف
                      </Button>
                    </div>
                  )}
                  {currentEmployee.national_id_file && (
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-sm">الهوية الوطنية</p>
                      <Button variant="link" size="sm" className="p-0 h-auto">
                        عرض الملف
                      </Button>
                    </div>
                  )}
                  {currentEmployee.contract_file && (
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-sm">العقد</p>
                      <Button variant="link" size="sm" className="p-0 h-auto">
                        عرض الملف
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* تحليل الأداء */}
        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                تحليل الأداء وتقييم الكفاءة
              </CardTitle>
              <CardDescription>
                عرض تفصيلي لأداء الموظف وتقييم كفاءته في مختلف جوانب العمل
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmployeePerformanceAnalysis employeeId={currentEmployee.id} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* حوار تغيير كلمة المرور */}
      <PasswordChangeDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        employeeId={currentEmployee.id}
        currentEmail={currentEmployee.email}
      />
    </div>
  );
}